import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { EllipsisVertical, Plus, SlidersHorizontal } from 'lucide-react';
import { useCustomFields, type CustomField } from '@/hooks/useCustomFields';
import { Checkbox } from '../ui/checkbox';
import ScrollArea from '../ui/shared/ScrollArea';
import { SettingsCard } from './SettingsCard';
import { useTranslation } from "react-i18next";

const inputClassName =
  'h-11 min-h-[36px] rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA] dark:focus-visible:ring-[#2563EB]/30';

const CustomFieldsTab: React.FC = () => {
  const { t } = useTranslation("settings");
  const { fields, loading, createField, updateField, deleteField } = useCustomFields();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    field_type: 'text' as CustomField['field_type'],
    is_active: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingField) {
      const success = await updateField(editingField.id, { ...formData, is_required: false, options: [] });
      if (success) {
        setDialogOpen(false);
        resetForm();
      }
    } else {
      const created = await createField({ ...formData, is_required: false, options: [] });
      if (created) {
        setDialogOpen(false);
        resetForm();
      }
    }
  };

  const handleEdit = (field: CustomField) => {
    setEditingField(field);
    setFormData({
      name: field.name,
      field_type: 'text',
      is_active: field.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm(t("customFields.confirmDelete"))) {
      await deleteField(id);
    }
  };

  const resetForm = () => {
    setEditingField(null);
    setFormData({
      name: '',
      field_type: 'text',
      is_active: true
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h2 className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{t("customFields.title")}</h2>
          <p className="text-sm text-[#64748B] dark:text-[#71717A]">{t("customFields.subtitle")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-[#2563EB] px-4 text-sm text-white shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8] dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]">
              <Plus className="w-4 h-4 mr-2" />
              {t("customFields.new")}
            </Button>
          </DialogTrigger>
          <DialogContent className="flex w-full p-0 rounded-2xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA] flex-col max-w-[500px]">
            <ScrollArea classNames={{ root: 'max-h-[90dvh] p-5 overflow-auto' }}>
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
                  {editingField ? 'Editar Campo' : 'Novo Campo'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="w-full mt-4 gap-4 flex flex-col">
                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="name" className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
                    {t("customFields.fieldName")}
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={inputClassName}
                    required
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: !!checked })}
                  />
                  <Label htmlFor="is_active" className="cursor-pointer text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
                    {t("customFields.activeField")}
                  </Label>
                </div>

                <div className="flex gap-2 justify-end mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#334155] transition-colors duration-200 hover:bg-[#F1F5F9] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    className="rounded-xl bg-[#2563EB] text-sm text-white shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8] dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]"
                  >
                    {editingField ? 'Salvar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>

      <SettingsCard>
        {loading ? (
          <div className="divide-y divide-[#E5E7EB] dark:divide-[#3F3F464D]" aria-busy="true" aria-label={t("customFields.loadingAria")}>
            {[0, 1, 2].map((i) => (
              <div key={i} className="p-4 flex items-center justify-between">
                <div className="h-4 w-40 rounded bg-[#F1F5F9] animate-pulse dark:bg-[#3F3F46]" />
                <div className="h-[34px] w-[34px] rounded-lg bg-[#F1F5F9] animate-pulse dark:bg-[#3F3F46]" />
              </div>
            ))}
          </div>
        ) : fields.length === 0 ? (
          <div className="p-10 flex flex-col items-center text-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/15 dark:text-[#60A5FA]">
              <SlidersHorizontal className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("customFields.empty")}</p>
              <p className="text-sm text-[#64748B] dark:text-[#71717A] mt-1">
                {t("customFields.emptyHint")}
              </p>
            </div>
            <Button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="mt-2 rounded-xl bg-[#2563EB] px-4 text-sm text-white shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8]"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t("customFields.new")}
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-[#E5E7EB] dark:divide-[#3F3F464D]">
            {fields.map((field) => (
              <div
                key={field.id}
                className="p-4 flex items-center justify-between bg-white transition-colors duration-200 hover:bg-[#F8FAFC] dark:bg-transparent dark:hover:bg-[#3F3F46]"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA] truncate">{field.name}</h3>
                    {!field.is_active && (
                      <span className="text-xs px-2 py-1 rounded-full bg-[#FEF2F2] text-[#DC2626] dark:bg-red-500/15 dark:text-[#F87171]">
                        {t("common.inactive")}
                      </span>
                    )}
                  </div>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label={t("customFields.fieldActions")}
                      className="flex justify-center items-center rounded-lg border border-[#E5E7EB] bg-white w-[34px] h-[34px] text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:text-[#A1A1AA] dark:hover:bg-[#27272A] dark:hover:text-[#60A5FA]"
                    >
                      <EllipsisVertical size={18} className="text-current" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="!bg-white p-1 w-[95px] flex flex-col gap-1 border border-[#E5E7EB] rounded-xl shadow-md dark:!bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none">
                    <button
                      type="button"
                      onClick={() => handleEdit(field)}
                      className="w-full flex justify-center rounded-lg items-center bg-transparent hover:bg-[#F1F5F9] min-h-[29px] text-sm text-[#0F172A] transition-colors duration-200 dark:hover:bg-[#3F3F46] dark:text-[#FAFAFA]"
                    >
                      {t("common.edit")}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(field.id)}
                      className="w-full flex justify-center rounded-lg items-center bg-transparent hover:bg-[#FEF2F2] min-h-[29px] text-sm text-[#DC2626] transition-colors duration-200 dark:hover:bg-red-500/10 dark:text-[#F87171]"
                    >
                      {t("common.delete")}
                    </button>
                  </PopoverContent>
                </Popover>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>
    </div>
  );
};

export default CustomFieldsTab;
